<script lang="typescript">
    import { state } from "./stores";
    import { getVariantProductCodes, getBaseLensImage, getBaseLensName } from "./utils";
    import { lensOption, logoImages } from "./appData";
    import type { Lens, LensVariant, LensCode, LensSubCategory, LogoImage } from "./types";
    import ProductCode from "./ProductCode.svelte";

    export let selectedLens: Lens;
    export let primaryVariant: LensVariant | null;
    export let secondaryVariant: LensVariant | null;
    export let image: string = "";

    const isAntiFogAndAdaptive: (a: LensVariant | null, b: LensVariant | null) => boolean = (
        primaryVariant,
        secondaryVariant
    ) =>
        primaryVariant !== null &&
        secondaryVariant !== null &&
        primaryVariant.optionType === lensOption.ANTIFOG &&
        secondaryVariant.optionType === lensOption.ADAPTIVE;

    const isAntiFog: (a: LensVariant | null) => boolean = (variant) =>
        variant !== null && variant.optionType === lensOption.ANTIFOG;

    const isTintUv: (a: LensVariant | null) => boolean = (variant) =>
        variant !== null && variant.optionType === lensOption.TINT_UV;

    const isAdaptive: (a: LensVariant | null) => boolean = (variant) =>
        variant !== null && variant.optionType === lensOption.ADAPTIVE;

    const isAntiReflect: (a: LensVariant | null) => boolean = (variant) =>
        variant !== null && variant.optionType === lensOption.PLUS;

    const isBlue: (a: LensVariant | null) => boolean = (variant) =>
        variant !== null && variant.optionType === lensOption.BLUE;

    const isTransitions: (a: LensVariant | null) => boolean = (variant) =>
        variant !== null && variant.optionType === lensOption.TRANSITIONS;

    const isTransitionsXtractive: (a: LensVariant | null) => boolean = (variant) =>
        variant !== null && variant.optionType === lensOption.TRANSITIONS_XTRA;

    const isPolarized: (a: LensVariant | null) => boolean = (variant) =>
        variant !== null && variant.optionType === lensOption.TRANSITIONS_XTRA_POLARIZED;

    let code: LensCode | LensSubCategory[];
    let lensName = getBaseLensName(selectedLens, $state.region);
    if (primaryVariant === null || isTintUv(primaryVariant)) {
        code = selectedLens.code;
    } else if (isAntiFogAndAdaptive(primaryVariant, secondaryVariant)) {
        code = getVariantProductCodes(secondaryVariant);
    } else if (
        (isAntiReflect(primaryVariant) && isTransitions(secondaryVariant)) ||
        (isTransitions(primaryVariant) && isAntiReflect(secondaryVariant))
    ) {
        code = getVariantProductCodes(secondaryVariant);
    } else if (
        (isAntiReflect(primaryVariant) && isTransitionsXtractive(secondaryVariant)) ||
        (isTransitionsXtractive(primaryVariant) && isAntiReflect(secondaryVariant))
    ) {
        code = getVariantProductCodes(secondaryVariant);
    } else if (
        (isPolarized(primaryVariant) && isTransitionsXtractive(secondaryVariant)) ||
        (isTransitionsXtractive(primaryVariant) && isPolarized(secondaryVariant))
    ) {
        code = getVariantProductCodes(secondaryVariant);
    } else {
        code = getVariantProductCodes(primaryVariant);
    }

    let variantName: string | null;
    if (primaryVariant === null) {
        variantName = null;
    } else {
        if (isTintUv(primaryVariant)) {
            variantName = null;
        } else if (isTransitions(primaryVariant)) {
            variantName = null;
        } else if (isTransitionsXtractive(primaryVariant)) {
            variantName = null;
        } else if (isAntiFogAndAdaptive(primaryVariant, secondaryVariant)) {
            variantName = "Anti-Fog";
        } else if (code == "SV24") {
            lensName = primaryVariant.name;
            variantName = null;
        } else {
            variantName = primaryVariant.name;
        }
    }

    export let logo: LogoImage | null = null;
    if (isTransitions(primaryVariant) || isTransitions(secondaryVariant)) {
        logo = logoImages.transitions;
    }
    if (isTransitionsXtractive(primaryVariant) || isTransitionsXtractive(secondaryVariant)) {
        if (isPolarized(primaryVariant) || isPolarized(secondaryVariant)) {
            logo = logoImages.xtractivePolarized;
        } else {
            logo = logoImages.xtractive;
        }
    }
    if (!image) {
        image = getBaseLensImage(selectedLens, $state.region);
    }
</script>

<div class="local-container">
    <img src={image} class="w-32 lg:w-36 object-contain" alt="spectacle lens" />
    <div class="w-full lg:pl-8 mt-4 lg:mt-0 flex flex-col justify-center">
        <div class="w-full mr-auto flex justify-between flex-col lg:flex-row lg:pr-2 lg:items-end">
            <h1 class="inline text-3xl lg:text-4xl">
                {@html lensName}
                {#if variantName !== null}
                    <br />+ {@html variantName}
                {/if}
                {#if logo !== null}
                    <br />+
                    <img src={logo.src} alt={logo.alt_text} class="inline h-14 mb-3" />
                {/if}
            </h1>
        </div>
        {#if isAntiFog(primaryVariant) || isAntiFog(secondaryVariant)}
            <p class="mt-4">*Price includes Anti-Fog lens solution (add code 10502986 to order)</p>
        {/if}
        <ProductCode classes="mt-4" {code} />
    </div>
</div>

<style>
    .local-container {
        @apply transition-shadow hover:shadow-md flex justify-between;
        @apply w-full bg-background p-6 lg:p-8 lg:pb-6 lg:pr-4 items-start mt-8;
        @apply flex-col lg:flex-row;
    }
</style>
